import { EyeOutlined } from '@ant-design/icons';
import {
  App,
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import CustomizedUpload from 'components/customized-upload';
import { UPLOAD_TYPE } from 'constants/common';
import { BannerPreview, BannerSizeNote } from 'pages/banners-manager/component/BannerSizeGuide';
import { setShowOnHome, setStatus, updateEvent } from '../api';
import {
  EventInput,
  GalaCategory,
  GalaEvent,
  GalaEventStatus,
  STATUS_META,
  errorText,
  fromVnInput,
  toVnInput,
} from '../contract';
import styles from '../styles.module.scss';

/**
 * Địa chỉ app web (REACT_APP_WEB_URL). Máy dev mặc định cổng 3002; bản build production không đặt
 * biến này thì ẩn nút "Xem trước trên web" (app web chưa có địa chỉ thật). Web bỏ qua tham số nào nó chưa hiểu.
 */
const WEB_URL =
  process.env.REACT_APP_WEB_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:3002/' : '');

const previewUrl = (eventId: number) => {
  const base = WEB_URL.replace(/[?#].*$/, '');
  return `${base}?screen=GALA&eventId=${eventId}&preview=1`;
};

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';
/** Khung ngày giờ chỉ dùng chữ "tường" (giờ VN) nên không phụ thuộc múi giờ của máy admin. */
const WALL = 'YYYY-MM-DD HH:mm';

type UploadValue = { fileKey: string; fileUrl: string; error?: string };

interface FormValues {
  title: string;
  eyebrow?: string;
  subtitle?: string;
  eventAt?: Dayjs | null;
  doorsOpenAt?: Dayjs | null;
  tbaText?: string;
  venueName?: string;
  venueAddress?: string;
  mapUrl?: string;
  venueVisibility: 'ALL' | 'TICKET';
  cover?: UploadValue;
  hero?: UploadValue;
  inviteTotal?: number | null;
  homeCta?: string;
  qrOnCustom: boolean;
  podiumCategoryId?: number | null;
}

const toPicker = (iso: string | null): Dayjs | null => {
  const wall = toVnInput(iso);
  return wall ? dayjs(wall.replace(' ', 'T')) : null;
};

const fromPicker = (v: Dayjs | null | undefined): string | null =>
  v ? fromVnInput(v.format(WALL)) : null;

const blankToNull = (v?: string | null): string | null => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

const toUpload = (key: string | null, url: string | null): UploadValue | undefined =>
  key ? { fileKey: key, fileUrl: url ?? '' } : undefined;

const uploadRule = {
  validator: (_: unknown, v?: UploadValue) =>
    v?.error ? Promise.reject(new Error(v.error)) : Promise.resolve(),
};

const URL_RULES = [
  { max: 500, message: 'Không quá 500 ký tự.' },
  { pattern: /^https?:\/\//i, message: 'Đường dẫn phải bắt đầu bằng http:// hoặc https://' },
];

interface Props {
  event: GalaEvent;
  /** Hạng mục của sự kiện — dùng cho ô chọn hạng mục bục Top 3. */
  categories?: GalaCategory[];
  onChanged: () => void | Promise<void>;
}

/** Tab Thông tin: form sự kiện + công bố / kết thúc + xem trước. */
const InfoTab = ({ event, categories = [], onChanged }: Props) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [homeBusy, setHomeBusy] = useState(false);
  // Form có sửa mà chưa lưu (bật khi đổi bất kỳ ô nào, tắt khi lưu xong).
  const [dirty, setDirty] = useState(false);
  const cover = Form.useWatch('cover', form);
  const hero = Form.useWatch('hero', form);

  const initialValues: FormValues = {
    title: event.title,
    eyebrow: event.eyebrow ?? '',
    subtitle: event.subtitle ?? '',
    eventAt: toPicker(event.eventAt),
    doorsOpenAt: toPicker(event.doorsOpenAt),
    tbaText: event.tbaText ?? '',
    venueName: event.venueName ?? '',
    venueAddress: event.venueAddress ?? '',
    mapUrl: event.mapUrl ?? '',
    venueVisibility: event.venueVisibility,
    cover: toUpload(event.coverImage, event.coverImageUrl),
    hero: toUpload(event.heroImage, event.heroImageUrl),
    inviteTotal: event.inviteTotal,
    homeCta: event.homeCta ?? '',
    qrOnCustom: event.qrOnCustom,
    podiumCategoryId: event.podiumCategoryId ?? null,
  };

  /**
   * Kiểm tra + lưu form. Trả true khi lưu được. `quiet`: không báo "Đã lưu" (dùng khi
   * lưu kèm đổi trạng thái); ô sai thì báo `invalidMsg` (để trống = chỉ tô đỏ ô sai).
   */
  const saveForm = async (
    opts: { quiet?: boolean; invalidMsg?: string } = {},
  ): Promise<boolean> => {
    let v: FormValues;
    try {
      v = await form.validateFields();
    } catch {
      if (opts.invalidMsg) message.error(opts.invalidMsg);
      return false;
    }
    const input: EventInput = {
      title: v.title.trim(),
      eyebrow: blankToNull(v.eyebrow),
      subtitle: blankToNull(v.subtitle),
      eventAt: fromPicker(v.eventAt),
      doorsOpenAt: fromPicker(v.doorsOpenAt),
      tbaText: blankToNull(v.tbaText),
      venueName: blankToNull(v.venueName),
      venueAddress: blankToNull(v.venueAddress),
      mapUrl: blankToNull(v.mapUrl),
      venueVisibility: v.venueVisibility,
      coverImage: v.cover?.fileKey || null,
      heroImage: v.hero?.fileKey || null,
      inviteTotal: typeof v.inviteTotal === 'number' ? v.inviteTotal : null,
      homeCta: blankToNull(v.homeCta),
      qrOnCustom: v.qrOnCustom,
    };
    // Bục Top 3: chỉ gửi khi đổi; id không còn trong danh sách hạng mục (đã xoá) coi như trống.
    const podium =
      typeof v.podiumCategoryId === 'number' && categories.some((c) => c.id === v.podiumCategoryId)
        ? v.podiumCategoryId
        : null;
    if (podium !== (event.podiumCategoryId ?? null)) input.podiumCategoryId = podium;
    setSaving(true);
    try {
      await updateEvent(event.id, input);
      setDirty(false);
      if (!opts.quiet) message.success('Đã lưu thông tin sự kiện.');
      return true;
    } catch (e) {
      message.error(errorText(e, 'Không lưu được. Kiểm tra lại các ô rồi thử lại.'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (await saveForm()) await onChanged();
  };

  /**
   * Đổi trạng thái. Form còn sửa dở thì LƯU TRƯỚC rồi mới đổi: công bố (và push
   * "Bạn có vé mời") phải dùng tiêu đề / ngày giờ mới, không dùng bản cũ trên máy chủ.
   */
  const changeStatus = async (status: GalaEventStatus, done: string) => {
    setStatusBusy(true);
    try {
      let savedFirst = false;
      if (dirty) {
        savedFirst = await saveForm({
          quiet: true,
          invalidMsg: 'Lưu thay đổi trước khi đổi trạng thái: còn ô chưa hợp lệ (tô đỏ).',
        });
        if (!savedFirst) return;
      }
      await setStatus(event.id, status);
      message.success(savedFirst ? `Đã lưu thay đổi. ${done}` : done);
      await onChanged();
    } catch (e) {
      message.error(errorText(e, 'Không đổi được trạng thái.'));
    } finally {
      setStatusBusy(false);
    }
  };

  const toggleHome = async (on: boolean) => {
    setHomeBusy(true);
    try {
      await setShowOnHome(event.id, on);
      message.success(on ? 'Đã bật thẻ Gala ở Trang chủ.' : 'Đã tắt thẻ Gala ở Trang chủ.');
      await onChanged();
    } catch (e) {
      message.error(errorText(e, 'Không đổi được công tắc Trang chủ.'));
    } finally {
      setHomeBusy(false);
    }
  };

  const status = event.status;

  return (
    <div className={styles.formWrap}>
      <div className={`${styles.card} ${styles.statusBar}`}>
        <span>
          Trạng thái: <strong>{STATUS_META[status].text}</strong>
        </span>
        {status !== 'PUBLISHED' && (
          <Popconfirm
            title="Công bố sự kiện?"
            description={
              dirty
                ? 'Thay đổi chưa lưu trong form sẽ được lưu trước, rồi app hiện sự kiện cho mọi người.'
                : 'App sẽ hiện sự kiện cho mọi người.'
            }
            okText="Công bố"
            cancelText="Không"
            onConfirm={() => changeStatus('PUBLISHED', 'Đã công bố sự kiện.')}
          >
            <Button type="primary" loading={statusBusy}>
              {status === 'ENDED' ? 'Công bố lại' : 'Công bố'}
            </Button>
          </Popconfirm>
        )}
        {status === 'PUBLISHED' && (
          <Popconfirm
            title="Kết thúc sự kiện?"
            description="Sự kiện sẽ không còn nhắc lịch; app vẫn giữ trang để xem lại."
            okText="Kết thúc"
            cancelText="Không"
            onConfirm={() => changeStatus('ENDED', 'Đã kết thúc sự kiện.')}
          >
            <Button loading={statusBusy}>Kết thúc</Button>
          </Popconfirm>
        )}
        {status !== 'DRAFT' && (
          <Popconfirm
            title="Đưa sự kiện về bản nháp?"
            description="App sẽ ẩn sự kiện (chỉ Admin xem được bằng chế độ xem trước)."
            okText="Về nháp"
            cancelText="Không"
            onConfirm={() => changeStatus('DRAFT', 'Đã đưa sự kiện về bản nháp.')}
          >
            <Button danger loading={statusBusy}>
              Về nháp
            </Button>
          </Popconfirm>
        )}
        {WEB_URL && (
          <Tooltip title="Mở app web, vào mục Gala">
            <Button
              icon={<EyeOutlined />}
              onClick={() => window.open(previewUrl(event.id), '_blank', 'noopener,noreferrer')}
            >
              Xem trước trên web
            </Button>
          </Tooltip>
        )}
        <Space>
          <Switch
            checked={event.showOnHome}
            loading={homeBusy}
            onChange={toggleHome}
            aria-label="Hiện thẻ Gala ở Trang chủ"
          />
          <span>Hiện ở Trang chủ</span>
        </Space>
      </div>

      {status === 'DRAFT' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Đây là bản nháp: app chưa hiện sự kiện. Điền xong bấm Công bố."
        />
      )}

      <div className={styles.card}>
        <Form<FormValues>
          form={form}
          layout="vertical"
          initialValues={initialValues}
          requiredMark="optional"
          onValuesChange={() => setDirty(true)}
        >
          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[
              { required: true, whitespace: true, message: 'Nhập tiêu đề sự kiện.' },
              { max: 200, message: 'Không quá 200 ký tự.' },
            ]}
          >
            <Input placeholder="HP GALA 2026" />
          </Form.Item>
          <div className={styles.row2}>
            <Form.Item
              name="eyebrow"
              label="Dòng nhỏ phía trên tiêu đề"
              rules={[{ max: 120, message: 'Không quá 120 ký tự.' }]}
            >
              <Input placeholder="Ví dụ: Đêm vinh danh Creator" />
            </Form.Item>
            <Form.Item
              name="subtitle"
              label="Phụ đề"
              rules={[{ max: 300, message: 'Không quá 300 ký tự.' }]}
            >
              <Input />
            </Form.Item>
          </div>

          <div className={styles.row2}>
            <Form.Item name="eventAt" label="Ngày giờ diễn ra (giờ Việt Nam)">
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format={DATE_FORMAT}
                style={{ width: '100%' }}
                placeholder="Chưa chốt ngày"
              />
            </Form.Item>
            <Form.Item name="doorsOpenAt" label="Giờ đón khách (giờ Việt Nam)">
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format={DATE_FORMAT}
                style={{ width: '100%' }}
                placeholder="Không bắt buộc"
              />
            </Form.Item>
          </div>
          <Form.Item
            name="tbaText"
            label="Lời nhắn khi chưa chốt ngày"
            extra="Chỉ hiện thay cho đồng hồ đếm ngược khi để trống ô ngày giờ."
            rules={[{ max: 300, message: 'Không quá 300 ký tự.' }]}
          >
            <Input placeholder="Ví dụ: Ngày giờ sẽ được công bố sớm" />
          </Form.Item>

          <div className={styles.row2}>
            <Form.Item
              name="venueName"
              label="Địa điểm"
              rules={[{ max: 200, message: 'Không quá 200 ký tự.' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="venueAddress"
              label="Địa chỉ"
              rules={[{ max: 500, message: 'Không quá 500 ký tự.' }]}
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="mapUrl" label="Link bản đồ" rules={URL_RULES}>
            <Input placeholder="https://maps.google.com/..." />
          </Form.Item>
          <Form.Item name="venueVisibility" label="Ai thấy địa điểm">
            <Radio.Group>
              <Radio value="ALL">Ai cũng thấy</Radio>
              <Radio value="TICKET">Chỉ người có vé</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="Ảnh bìa (thẻ ở Trang chủ)" required={false} style={{ marginBottom: 0 }}>
            <BannerSizeNote />
          </Form.Item>
          <Form.Item name="cover" rules={[uploadRule]}>
            <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
          </Form.Item>
          <BannerPreview src={cover?.fileUrl || undefined} />

          <Form.Item
            name="hero"
            label="Ảnh hero (nền đầu trang Gala)"
            extra="Ảnh dọc tỉ lệ 4:5, JPG hoặc PNG, nên dưới 1 MB."
            rules={[uploadRule]}
          >
            <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
          </Form.Item>
          {hero?.fileUrl && <img className={styles.heroPreview} src={hero.fileUrl} alt="" />}

          <div className={styles.row2} style={{ marginTop: 16 }}>
            <Form.Item
              name="inviteTotal"
              label="Số lời mời"
              extra={'Hiện ở đầu trang ("100 lời mời"). Để trống để ẩn.'}
            >
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="homeCta"
              label="Chữ trên nút ở Trang chủ"
              rules={[{ max: 80, message: 'Không quá 80 ký tự.' }]}
            >
              <Input placeholder="Ví dụ: Xem chi tiết" />
            </Form.Item>
          </div>
          <Form.Item
            name="qrOnCustom"
            label="Vé làm sẵn (ảnh riêng)"
            valuePropName="checked"
            extra="Bật thì hệ thống vẽ thêm QR và mã nhập tay vào góc ảnh vé."
          >
            <Switch checkedChildren="Vẽ QR" unCheckedChildren="Không vẽ" />
          </Form.Item>
          <Form.Item
            name="podiumCategoryId"
            label="Hạng mục cho bục Top 3"
            extra="Bục Top 3 trên trang Gala lật mặt theo hạng mục này khi đã công bố người thắng. Để trống = hạng mục mã 01."
          >
            <Select
              allowClear
              placeholder="Mặc định: hạng mục mã 01"
              options={categories.map((c) => ({ value: c.id, label: `${c.code} — ${c.nameVi}` }))}
            />
          </Form.Item>

          <Button type="primary" loading={saving} onClick={onSave}>
            Lưu thông tin
          </Button>
        </Form>
      </div>
    </div>
  );
};

export default InfoTab;
